import { memo, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import './BottomNav.css';

// Primary slots ordered by operator-usage frequency. Receivers promoted in
// because it's the funnel into Performance/Remote Config — second-most-
// touched surface after Live. Messages moved into More — most users open it
// only when actively investigating ACARS traffic.
const primaryNavItems = [
  { id: 'map', label: 'Live', icon: '/icons/map.png' },
  { id: 'receivers', label: 'Receivers', icon: '/icons/location.png' },
  { id: 'history', label: 'History', icon: '/icons/history.svg' },
  // Predictions is desktop-only: not offered on phones. The Airport board is
  // its phone-sized counterpart (same flights, status only).
  { id: 'airport', label: 'Airport', icon: '/icons/airport_white.svg' },
];

// Keep in sync with SideRail (tablet) / Navigation (desktop). Airband +
// Statistics were added to those but missed here, so on phones the "More"
// sheet had no way to reach them.
const secondaryNavItems = [
  { id: 'messages', label: 'Messages', icon: '/icons/messages.svg' },
  { id: 'airband', label: 'Airband', icon: '/icons/messages.svg' },
  { id: 'statistics', label: 'Statistics', icon: '/icons/history.svg' },
  { id: 'notams', label: 'NOTAMs', icon: '/icons/notam.svg' },
  { id: 'account', label: 'Account', icon: '/icons/person.png' },
  { id: 'settings', label: 'Settings', icon: '/icons/settings.svg' },
];

function BottomNav({ currentPage, onPageChange, isViewerOnly = false }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const handleItemClick = useCallback((pageId) => {
    if (onPageChangeRef.current) {
      onPageChangeRef.current(pageId);
    }
    setMoreOpen(false);
  }, []);

  // Debounced toggle: on iOS both onTouchEnd and onClick can fire for one tap
  // (despite preventDefault), which toggled the sheet open+closed in the same
  // instant — the "More does nothing" bug. Ignore the second event.
  const lastMoreToggleRef = useRef(0);
  const handleMoreClick = useCallback(() => {
    const now = Date.now();
    if (now - lastMoreToggleRef.current < 350) return;
    lastMoreToggleRef.current = now;
    setMoreOpen(prev => !prev);
  }, []);

  // Close sheet when navigating away
  useEffect(() => {
    setMoreOpen(false);
  }, [currentPage]);

  // Filter secondary items for viewer-only users
  const filteredSecondary = useMemo(() => {
    if (isViewerOnly) {
      return secondaryNavItems.filter(item => !['account', 'receivers'].includes(item.id));
    }
    return secondaryNavItems;
  }, [isViewerOnly]);

  // Check if current page is a secondary item (to highlight "More" tab)
  const isSecondaryActive = useMemo(() => {
    return filteredSecondary.some(item => item.id === currentPage);
  }, [currentPage, filteredSecondary]);

  return (
    <>
      {/* More sheet backdrop */}
      {moreOpen && (
        <div
          className="more-sheet-backdrop"
          onClick={() => setMoreOpen(false)}
          onTouchEnd={(e) => { e.preventDefault(); setMoreOpen(false); }}
        />
      )}

      {/* More sheet */}
      <div className={`more-sheet ${moreOpen ? 'open' : ''}`}>
        {filteredSecondary.map((item) => (
          <button
            key={item.id}
            className={`more-sheet-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => handleItemClick(item.id)}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleItemClick(item.id);
            }}
          >
            <img src={item.icon} alt="" className="more-sheet-icon" />
            <span className="more-sheet-label">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Bottom nav bar */}
      <nav className="bottom-nav">
        {primaryNavItems.map((item) => (
          <button
            key={item.id}
            className={`bottom-nav-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => handleItemClick(item.id)}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleItemClick(item.id);
            }}
          >
            <img src={item.icon} alt="" className="bottom-nav-icon" />
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        ))}

        {/* More button */}
        <button
          className={`bottom-nav-item ${isSecondaryActive ? 'active' : ''} ${moreOpen ? 'more-open' : ''}`}
          onClick={handleMoreClick}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleMoreClick();
          }}
        >
          <svg className="bottom-nav-icon-svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
          <span className="bottom-nav-label">More</span>
        </button>
      </nav>
    </>
  );
}

export default memo(BottomNav);
